import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  durationMs: number;
  action?: ToastAction;
}

interface ToastPushOpts {
  tone?: ToastTone;
  /** Override the 4000ms auto-dismiss (e.g. 5000 for undo windows). */
  durationMs?: number;
  /** Optional action button rendered inline (e.g. "Undo"). */
  action?: ToastAction;
}

interface ToastContextValue {
  push: (message: string, opts?: ToastPushOpts) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const AUTO_DISMISS_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, opts?: ToastPushOpts) => {
    const id = Date.now() + Math.random();
    const tone: ToastTone = opts?.tone ?? 'info';
    const durationMs = opts?.durationMs ?? AUTO_DISMISS_MS;
    setToasts((cur) => [...cur, { id, message, tone, durationMs, action: opts?.action }]);
  }, []);
  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Two viewports so errors get assertive announcement while
          info/success stay polite — a single role="status" container
          would force everything to one or the other. */}
      <div className="toast-viewport" role="status" aria-live="polite" aria-atomic="false">
        {toasts.filter((t) => t.tone !== 'error').map((t) => (
          <ToastBubble
            key={t.id}
            toast={t}
            onDismiss={() => setToasts((cur) => cur.filter((x) => x.id !== t.id))}
          />
        ))}
      </div>
      <div className="toast-viewport" role="alert" aria-live="assertive" aria-atomic="true">
        {toasts.filter((t) => t.tone === 'error').map((t) => (
          <ToastBubble
            key={t.id}
            toast={t}
            onDismiss={() => setToasts((cur) => cur.filter((x) => x.id !== t.id))}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Each toast manages its own dismiss timer so hover can pause it.
// Sleep-deprived readers need a beat to read; pause-on-hover gives that
// without inflating the global timeout.
function ToastBubble({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const timerRef = useRef<number | null>(null);
  const remainingRef = useRef<number>(toast.durationMs);
  const startedAtRef = useRef<number>(0);

  const start = useCallback(
    (ms: number) => {
      startedAtRef.current = Date.now();
      timerRef.current = window.setTimeout(onDismiss, ms);
    },
    [onDismiss],
  );

  const pause = useCallback(() => {
    if (timerRef.current == null) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    remainingRef.current = Math.max(800, remainingRef.current - (Date.now() - startedAtRef.current));
  }, []);

  const resume = useCallback(() => {
    if (timerRef.current != null) return;
    start(remainingRef.current);
  }, [start]);

  useEffect(() => {
    start(toast.durationMs);
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, [start, toast.durationMs]);

  function handleAction(): void {
    if (!toast.action) return;
    // Stop the timer so the action callback runs before the toast unmounts.
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    toast.action.onClick();
    onDismiss();
  }

  return (
    <div
      className="toast"
      data-tone={toast.tone}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      tabIndex={0}
    >
      <span className="toast-message">{toast.message}</span>
      {toast.action ? (
        <button
          type="button"
          className="toast-action"
          onClick={handleAction}
        >
          {toast.action.label}
        </button>
      ) : null}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
